import React, { useEffect, useState } from 'react';
import { Button, Dimmer, Icon, Loader, Modal } from 'semantic-ui-react';
import sendToast from '../../../utils/Toast';
import CreateOrderStepOne from './CreateOrderStepOne';
import CreateOrderStepTwo from './CreateOrderStepTwo';
import CreateOrderStepThree from './CreateOrderStepThree';
import '../ServiceOrderWidget/style.less';
import './style.less';

interface Props {
    open: boolean;
    onClose: () => void;
    FacilityId: string;
    residentId: string;
    refreshData: () => void;
    facilityPaymentsEnabled?: boolean;
}

const STEPS = [
    { step: 1, label: 'Category' },
    { step: 2, label: 'Service' },
    { step: 3, label: 'Details' },
];

// "New Request" create-order modal for the Order Status page — matches the
// order-modal wizard in order-status-page.html: select-then-click-Next
// navigation with a shared Back/Next footer on steps 1-2, driven by dedicated
// CreateOrderStep* components (kept separate from ServiceOrderWidget's
// Step1/2/3 so the full-page /create-order wizard used elsewhere is untouched).
const CreateOrderWizardModal: React.FC<Props> = ({ open, onClose, FacilityId, residentId, refreshData, facilityPaymentsEnabled = false }) => {
    const [isFetching, setIsFetching] = useState(false);
    const [stepForm, setStepForm] = useState<number>(1);
    const [selectedServiceId, setSelectedServiceId] = useState<string>('');
    const [selectedServiceCategoryName, setSelectedServiceCategoryName] = useState<string>('');
    const [selectedServiceCategoryHeading, setSelectedServiceCategoryHeading] = useState<string>('');

    const resetWizard = () => {
        // Order matters: stepForm must land on 1 before the selections are
        // cleared, otherwise (when this runs outside React's batching, e.g.
        // after an await in CreateOrderStepThree's submit handler) the
        // validation effect below can catch a transient "step 3, nothing
        // selected" state and fire spurious warning toasts.
        setStepForm(1);
        setSelectedServiceId('');
        setSelectedServiceCategoryName('');
        setSelectedServiceCategoryHeading('');
    };

    useEffect(() => {
        if (open) resetWizard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        const requirements = [
            { step: 2, field: selectedServiceCategoryName, fallback: 1, message: 'Service category is not selected' },
            { step: 3, field: selectedServiceCategoryName, fallback: 1, message: 'Service category is not selected' },
            { step: 3, field: selectedServiceId, fallback: 2, message: 'Service ID is not selected' },
        ];
        for (const rule of requirements) {
            if (stepForm === rule.step && !rule.field) {
                sendToast('warn', rule.message);
                setStepForm(rule.fallback);
                return;
            }
        }
    }, [stepForm, selectedServiceCategoryName, selectedServiceId]);

    const handleClose = () => {
        resetWizard();
        onClose();
    };

    const handleBack = () => {
        if (stepForm === 1) {
            handleClose();
        } else {
            setStepForm(stepForm - 1);
        }
    };

    const handleOrderCreated = () => {
        refreshData();
        handleClose();
    };

    const handleNext = () => {
        if (stepForm === 1) setStepForm(2);
        else if (stepForm === 2) setStepForm(3);
    };

    const isNextDisabled = stepForm === 1 ? !selectedServiceCategoryName : !selectedServiceId;

    return (
        <Modal open={open} onClose={handleClose} closeIcon={false} className="create-order-wizard-modal">
          <Modal.Content>
            <div className="order-modal-header">
                <div className="order-modal-title">New Request</div>
                <button type="button" className="order-modal-close" aria-label="Close" onClick={handleClose}>
                    <Icon name="close" />
                </button>
            </div>

            <div className="wizard-steps-page">
                {STEPS.map(({ step, label }, i) => (
                    <React.Fragment key={step}>
                        <div className={`wizard-step${stepForm === step ? ' active' : ''}${stepForm > step ? ' done' : ''}`}>
                            <div className="wizard-step-dot">
                                {stepForm > step ? <Icon name="check" size="small" /> : step}
                            </div>
                            <div className="wizard-step-label">{label}</div>
                        </div>
                        {i < STEPS.length - 1 && <div className="wizard-step-line" />}
                    </React.Fragment>
                ))}
            </div>

            <div className="order-modal-body-wrap">
                <Dimmer className="create-order-dimmer" active={isFetching} inverted>
                    <Loader active={isFetching} />
                </Dimmer>

                <div className="order-modal-body">
                {stepForm === 1 && (
                    <CreateOrderStepOne
                        FacilityId={FacilityId}
                        selectedServiceCategoryName={selectedServiceCategoryName}
                        setSelectedServiceCategoryName={setSelectedServiceCategoryName}
                        setSelectedServiceCategoryHeading={setSelectedServiceCategoryHeading}
                        setIsFetching={setIsFetching}
                    />
                )}

                {stepForm === 2 && (
                    <>
                        <div className="dept-crumb" onClick={handleBack}>
                            <Icon name="arrow left" />
                            <span>{selectedServiceCategoryHeading || selectedServiceCategoryName}</span>
                        </div>
                        <CreateOrderStepTwo
                            FacilityId={FacilityId}
                            selectedServiceCategoryName={selectedServiceCategoryName}
                            selectedResidentId={residentId}
                            selectedServiceId={selectedServiceId}
                            setSelectedServiceId={setSelectedServiceId}
                            setIsFetching={setIsFetching}
                            isFetching={isFetching}
                        />
                    </>
                )}

                {stepForm === 3 && (
                    <CreateOrderStepThree
                        FacilityId={FacilityId}
                        selectedServiceId={selectedServiceId}
                        setIsFetching={setIsFetching}
                        setStepForm={setStepForm}
                        selectedResidentId={residentId}
                        selectedServiceCategoryName={selectedServiceCategoryName}
                        setSelectedServiceId={setSelectedServiceId}
                        setSelectedServiceCategoryName={setSelectedServiceCategoryName}
                        refreshHandler={handleOrderCreated}
                        facilityPaymentsEnabled={facilityPaymentsEnabled}
                    />
                )}
                </div>
            </div>

            {stepForm !== 3 && (
                <div className="wizard-footer">
                    {stepForm > 1 && (
                        <Button type="button" className="wizard-btn-back" onClick={handleBack}>Back</Button>
                    )}
                    <Button type="button" className="wizard-btn-next" disabled={isNextDisabled} onClick={handleNext}>Next</Button>
                </div>
            )}
          </Modal.Content>
        </Modal>
    );
};

export default CreateOrderWizardModal;
